import classNames from 'classnames';
import ConfirmChangesDialog from 'components/organisms/NavigationBlocker/ConfirmChangesDialog';
import { useNavigationBlocker } from 'components/organisms/NavigationBlocker';
import { Button, TextField } from 'components/widgets';
import { useFormikContext } from 'formik-2';
import PropTypes from 'prop-types';
import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Translate } from 'react-localize-redux';
import { updateNavigationSections } from '../../actions/navigation';
import { getCanAccessFeature } from '../../selectors/navigation';
import NavigationService from '../../services/navigation';
import { trackMixpanelEvent } from '../../utils/general';
import WarningMessage from '../../components/utilities/WarningMessage';
import CategoryPicker from './CategoryPicker';
import DescriptionField from './DescriptionField';
import FieldLabel from './FieldLabel';
import LogoUpload from './LogoUpload';
import WebsiteField from './WebsiteField';
import css from '../page.css';
import service from '../service';

const PARTNER_ONBOARDING_MIXPANEL_PREFIX = 'Partner Onboarding';
const translationPrefix = 'STATIC.PAGES.PARTNER_INFORMATION';
const MAX_CATEGORIES = 2;
const PARTNER_INTEGRATION_PATH = '#/react/partners-integration';

const hasRequiredPartnerFields = ({ website, hasLogo }) => Boolean((website || '').trim()) && Boolean(hasLogo);

const navigationService = new NavigationService();

const PartnerInformationForm = ({ translate, setSavedProfile, shouldRedirectAfterFirstSaveRef }) => {
    const dispatch = useDispatch();
    const canAccessIntegration = useSelector(getCanAccessFeature('partners_integration'));
    const { values, dirty, initialValues, setFieldValue, resetForm } = useFormikContext();

    const [showCancelConfirm, setShowCancelConfirm] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const [logoError, setLogoError] = useState('');
    const [saveMessage, setSaveMessage] = useState(null);
    const [websiteFieldValid, setWebsiteFieldValid] = useState(false);

    const { allowNextNavigation } = useNavigationBlocker(dirty);

    const confirmCancel = () => {
        setShowCancelConfirm(false);
        setLogoError('');
        resetForm();
    };

    const isFormValid =
        hasRequiredPartnerFields({
            website: values.website,
            hasLogo: Boolean(values.logoFile || (values.logoUrl || '').trim()),
        }) &&
        websiteFieldValid &&
        !logoError;

    const handleSave = async () => {
        setIsSaving(true);
        setSaveMessage(null);

        try {
            let updatedLogoUrl = values.logoUrl;

            if (values.logoFile) {
                const data = await service.uploadPartnerLogo(values.logoFile);
                updatedLogoUrl = data.logo_url;
            }

            const hasProfileChanges =
                values.website !== initialValues.website ||
                values.description !== initialValues.description ||
                values.categories.length !== initialValues.categories.length ||
                !values.categories.every(c => initialValues.categories.includes(c));

            let updatedProfile;
            if (hasProfileChanges) {
                const data = await service.updatePartnerProfile({
                    website: values.website.trim(),
                    description: values.description.trim(),
                    categories: values.categories,
                });
                updatedProfile = {
                    companyName: data.company_name || '',
                    website: data.website || '',
                    description: data.description || '',
                    categories: data.categories || [],
                    logoUrl: data.logo_url || '',
                    logoFile: null,
                };
            } else {
                updatedProfile = {
                    ...values,
                    logoUrl: updatedLogoUrl,
                    logoFile: null,
                };
            }

            setSavedProfile(updatedProfile);
            resetForm({ values: updatedProfile });

            setSaveMessage({
                type: 'success',
                message: `${translationPrefix}.MESSAGES.SAVE_SUCCESS`,
            });

            trackMixpanelEvent(PARTNER_ONBOARDING_MIXPANEL_PREFIX, 'Partner Info Saved', {
                website: values.website.trim(),
                description: values.description.trim(),
                categories_selected: values.categories,
            });

            navigationService
                .getNavigation({ cache: false })
                .then(sections => dispatch(updateNavigationSections(sections)))
                .catch(() => {});

            if (shouldRedirectAfterFirstSaveRef.current && canAccessIntegration) {
                shouldRedirectAfterFirstSaveRef.current = false;
                allowNextNavigation();
                window.location.replace(PARTNER_INTEGRATION_PATH);
                return;
            }
            setIsSaving(false);
        } catch (err) {
            setIsSaving(false);

            const errorMessage = typeof err === 'string' ? err : err?.value;
            setSaveMessage({
                type: 'error',
                message: errorMessage || `${translationPrefix}.MESSAGES.SAVE_ERROR`,
            });
        }
    };

    const handleLogoFileChange = file => {
        setFieldValue('logoFile', file);
        setLogoError('');
    };

    const handleLogoRemove = () => {
        setFieldValue('logoUrl', '');
        setFieldValue('logoFile', null);
        setLogoError('');
    };

    const toggleCategory = category => {
        const { categories } = values;
        if (categories.includes(category)) {
            setFieldValue(
                'categories',
                categories.filter(c => c !== category)
            );
        } else if (categories.length < MAX_CATEGORIES) {
            setFieldValue('categories', [...categories, category]);
        }
    };

    return (
        <>
            {saveMessage && (
                <div className={css.saveMessageContainer}>
                    <WarningMessage
                        type={saveMessage.type}
                        message={saveMessage.message}
                        className={css.saveMessage}
                        showIcon
                        onClose={() => setSaveMessage(null)}
                    />
                </div>
            )}
            <div className={css.form}>
                <div className={css.fieldGroup}>
                    <FieldLabel icon="building" translationKey={`${translationPrefix}.COMPANY_NAME_LABEL`} />
                    <TextField
                        value={values.companyName}
                        onChange={() => {}}
                        disabled
                        containerClass={css.textFieldContainer}
                    />
                    <p className={css.fieldNote}>
                        <Translate id={`${translationPrefix}.COMPANY_NAME_NOTE`} />
                    </p>
                </div>
                <div className={css.fieldGroup}>
                    <WebsiteField
                        onValidityChange={setWebsiteFieldValid}
                        translationPrefix={translationPrefix}
                        containerClass={css.textFieldContainer}
                        translate={translate}
                    />
                </div>
                <div className={css.fieldGroup}>
                    <FieldLabel icon="upload" translationKey={`${translationPrefix}.LOGO_LABEL`} required />
                    <p className={css.fieldNote}>
                        <Translate id={`${translationPrefix}.LOGO_NOTE`} />
                    </p>
                    <LogoUpload
                        logoFile={values.logoFile}
                        logoUrl={values.logoUrl}
                        error={logoError}
                        onFileChange={handleLogoFileChange}
                        onValidationError={setLogoError}
                        onRemove={handleLogoRemove}
                    />
                </div>
                <div className={css.fieldGroup}>
                    <FieldLabel icon="file-description" translationKey={`${translationPrefix}.DESCRIPTION_LABEL`} />
                    <p className={css.fieldNote}>
                        <Translate id={`${translationPrefix}.DESCRIPTION_NOTE`} />
                    </p>
                    <DescriptionField
                        value={values.description}
                        onChange={value => setFieldValue('description', value)}
                    />
                </div>
                <div className={classNames(css.fieldGroup, css.categoriesFieldGroup)}>
                    <FieldLabel icon="layout-grid" translationKey={`${translationPrefix}.CATEGORIES_LABEL`} />
                    <p className={css.fieldNote}>
                        <Translate id={`${translationPrefix}.CATEGORIES_NOTE`} data={{ max: MAX_CATEGORIES }} />
                    </p>
                    <CategoryPicker selected={values.categories} max={MAX_CATEGORIES} onToggle={toggleCategory} />
                </div>
            </div>
            <div className={css.footer}>
                {(!isFormValid || !dirty) && (
                    <span className={css.footerNote}>
                        <Translate
                            id={
                                !isFormValid
                                    ? `${translationPrefix}.REQUIRED_FIELDS_NOTE`
                                    : `${translationPrefix}.NO_CHANGES_NOTE`
                            }
                        />
                    </span>
                )}
                {dirty && (
                    <Button
                        type="flat"
                        onClick={() => setShowCancelConfirm(true)}
                        disabled={isSaving}
                        text="STATIC.BUTTONS.CANCEL"
                    />
                )}
                <Button
                    type="primary"
                    onClick={handleSave}
                    disabled={!dirty || !isFormValid || isSaving}
                    showSpinner={isSaving}
                    text="STATIC.BUTTONS.SAVE"
                />
            </div>
            <ConfirmChangesDialog
                open={showCancelConfirm}
                onAccept={confirmCancel}
                onReject={() => setShowCancelConfirm(false)}
            />
        </>
    );
};

PartnerInformationForm.propTypes = {
    translate: PropTypes.func.isRequired,
    setSavedProfile: PropTypes.func.isRequired,
    shouldRedirectAfterFirstSaveRef: PropTypes.shape({ current: PropTypes.bool }).isRequired,
};

export default PartnerInformationForm;
